import React, { useEffect, useState, useTransition } from "react";
import { useAppDispatch, useAppSelector } from "../../_App/Redux/hooks";
import { Modal } from "react-bootstrap";
import { formatDateAndTime } from "../../_App/Helpers/helpers";
import { OrderType } from "../../_App/Types/Entites/Orders/OrderType";
import {
  clientOrders,
  findClientHistoriqueFidelite,
  findClientOrders,
  getClientFideliteTimeline,
  historiqueFidelite,
} from "../../_App/Redux/Slices/client/clientSlice";
import Loader from "../../_Layouts/Loader/Loader";
import { useTranslation } from "react-i18next";
import {
  getTicketById,
  Ticket,
} from "../../_App/Redux/Slices/tickets/TicketSlice";
import {
  findPaymentsByTicketApi,
  listOperationsByPaymentMethod,
} from "../../_App/Redux/Slices/payments/PaymentSlice";
import Barcode from "react-barcode";
import _ from "lodash";
import { FideliteTimelineItem } from "../../_App/Types/Entites/ClientType";

const FicheClient = ({ client, ShowModalClient, setShowModalClient }: any) => {
  const dispatch = useAppDispatch();
  const orders = useAppSelector(clientOrders);
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const ticket = useAppSelector(Ticket);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [dataLoaded, setDataLoaded] = useState(false);
  const { timeline, totalEarned, totalSpent, currentBalance } = useAppSelector(
    (state: any) => state.clients.fideliteTimeline,
  );

  useEffect(() => {
    if (client._id) {
      dispatch(getClientFideliteTimeline(client._id));
    }
  }, [client._id, dispatch]);

  const historiqueFideliteClient = useAppSelector(historiqueFidelite);
  const { t } = useTranslation();

  const handleClose = () => {
    setShowModalClient(false);
    setSelectedTicket(null);
    setDataLoaded(false);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (ShowModalClient && client?._id && !dataLoaded) {
      const loadData = async () => {
        setLoading(true);
        try {
          await dispatch(findClientHistoriqueFidelite(client._id));
          await dispatch(
            findClientOrders({
              data: {
                client: client._id,
                index: currentPage,
              },
            }),
          );
          setDataLoaded(true);
        } catch (error) {
          console.error("Error loading client data:", error);
        } finally {
          setLoading(false);
        }
      };
      loadData();
    }
  }, [ShowModalClient, client?._id, dispatch, currentPage, dataLoaded]);

  useEffect(() => {
    if (ShowModalClient && client?._id && dataLoaded && currentPage > 1) {
      const loadOrders = async () => {
        setLoading(true);
        await dispatch(
          findClientOrders({
            data: {
              client: client._id,
              index: currentPage,
            },
          }),
        );
        setLoading(false);
      };
      loadOrders();
    }
  }, [currentPage, ShowModalClient, client?._id, dispatch, dataLoaded]);

  useEffect(() => {
    if (ShowModalClient && selectedTicket) {
      dispatch(getTicketById({ id: undefined, order: selectedTicket }));
      dispatch(findPaymentsByTicketApi(selectedTicket));
    }
  }, [selectedTicket, ShowModalClient, dispatch]);

  const handleNextPage = () => {
    if (orders && currentPage < orders.totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const paymentDetails = useAppSelector(listOperationsByPaymentMethod);
  const orderItemsGroupedByTva = _.groupBy(
    ticket?.ticket_order?.items,
    "article.tva",
  );

  if (!client) return null;

  return loading === true ? (
    <Loader />
  ) : (
    <Modal
      show={ShowModalClient}
      onHide={handleClose}
      size="xl"
      centered
      className="client-fiche-modal"
    >
      <Modal.Header style={styles.modalHeader}>
        <Modal.Title>
          <div style={styles.modalTitle}>
            <i className="fa fa-user" style={{ marginRight: "12px" }}></i>
            <span>
              {t("Fiche Client")}{" "}
              <span style={styles.clientName}>
                {client?.nom} {client?.prenom}
              </span>
            </span>
          </div>
        </Modal.Title>
        <button onClick={handleClose} style={styles.closeButton}>
          <i className="fa fa-times"></i>
        </button>
      </Modal.Header>

      <Modal.Body style={styles.modalBody}>
        {/* Personal Details Section */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionIcon}>
              <i className="fa fa-user"></i>
            </div>
            <h3 style={styles.sectionTitle}>{t("Détails personnels")}</h3>
          </div>
          <div style={styles.infoGrid}>
            <InfoRow label={t("Nom")} value={client?.nom} />
            <InfoRow label={t("Prénom")} value={client?.prenom} />
            <InfoRow
              label={t("Date de naissance")}
              value={client?.date_naissance?.split("T")[0]}
            />
            <InfoRow label={t("Téléphone")} value={client?.mobile} />
          </div>
        </div>

        {/* Fidelity Section */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={{ ...styles.sectionIcon, backgroundColor: "#10b981" }}>
              <i className="fa fa-trophy" style={{ color: "white" }}></i>
            </div>
            <h3 style={styles.sectionTitle}>{t("Fidélité")}</h3>
          </div>
          <div style={styles.fidelityCards}>
            <div style={styles.fidelityCard}>
              <i
                className="fa fa-star"
                style={{ fontSize: "24px", color: "#f59e0b" }}
              ></i>
              <div style={styles.fidelityCardValue}>{client?.points || 0}</div>
              <div style={styles.fidelityCardLabel}>{t("Points")}</div>
            </div>
            <div style={styles.fidelityCard}>
              <i
                className="fa fa-money"
                style={{ fontSize: "24px", color: "#10b981" }}
              ></i>
              <div style={styles.fidelityCardValue}>
                {client?.equivalentMoney?.toFixed(3) || 0} TND
              </div>
              <div style={styles.fidelityCardLabel}>{t("Équivalence")}</div>
            </div>
            <div style={styles.fidelityCard}>
              <i
                className="fa fa-tag"
                style={{ fontSize: "24px", color: "#8b5cf6" }}
              ></i>
              <div style={styles.fidelityCardValue}>
                {client?.niveauFidelite?.libelle || "—"}
              </div>
              <div style={styles.fidelityCardLabel}>{t("Catégorie")}</div>
            </div>
          </div>
        </div>

        {/* Statistics Section */}
        <div style={styles.section}>
          <div style={styles.statsGrid}>
            <StatCard
              title={t("Total TTC")}
              value={`${orders?.totalOrders?.toFixed(3) || 0} TND`}
              icon="fa-shopping-bag"
              color="#3b82f6"
            />
            <StatCard
              title={t("Montant remises")}
              value={`${orders?.totalRemises?.toFixed(3) || 0} TND`}
              icon="fa-percent"
              color="#ef4444"
            />
            <StatCard
              title={t("Total Net")}
              value={`${orders?.totalNet?.toFixed(3) || 0} TND`}
              icon="fa-wallet"
              color="#10b981"
            />
            <StatCard
              title={t("Échanges / retours")}
              value={orders?.echanges + orders?.retours || 0}
              icon="fa-exchange"
              color="#8b5cf6"
            />
          </div>
        </div>

        {/* History Section */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionIcon}>
              <i className="fa fa-history"></i>
            </div>
            <h3 style={styles.sectionTitle}>{t("Historique des achats")}</h3>
          </div>

          <div style={styles.historyContainer}>
            {/* Orders Timeline */}
            <div style={styles.timelineColumn}>
              <div style={styles.timeline}>
                {orders?.orders?.map((item: OrderType, index: number) => (
                  <div
                    key={index}
                    style={{
                      ...styles.timelineItem,
                      ...(selectedTicket === item._id
                        ? styles.timelineItemActive
                        : {}),
                    }}
                    onClick={() => setSelectedTicket(item._id)}
                  >
                    <div style={styles.timelineDot} />
                    <div style={styles.timelineContent}>
                      <div style={styles.timelineDate}>
                        {formatDateAndTime(item.date)}
                      </div>
                      <div style={styles.timelineTitle}>
                        {t("Commande N°")} {item.order_number}
                      </div>
                      <div style={styles.timelineAmount}>
                        {item?.totalNet?.toFixed(3)} TND
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {orders?.totalPages > 1 && (
                <div style={styles.pagination}>
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    style={{
                      ...styles.pageButton,
                      ...(currentPage === 1 ? styles.pageButtonDisabled : {}),
                    }}
                  >
                    <i className="fa fa-chevron-left"></i>
                  </button>
                  <span style={styles.pageInfo}>
                    {t("Page")} {currentPage} / {orders?.totalPages || 1}
                  </span>
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === orders?.totalPages}
                    style={{
                      ...styles.pageButton,
                      ...(currentPage === orders?.totalPages
                        ? styles.pageButtonDisabled
                        : {}),
                    }}
                  >
                    <i className="fa fa-chevron-right"></i>
                  </button>
                </div>
              )}
            </div>

            {/* Ticket Details */}
            <div style={styles.ticketColumn}>
              {ticket ? (
                <div style={styles.ticketContainer}>
                  <div style={styles.ticketHeader}>
                    <i className="fa fa-receipt"></i>
                    <span>{t("Ticket de caisse")}</span>
                  </div>
                  <div style={styles.ticketContent}>
                    <div style={styles.ticketInfo}>
                      <div>
                        <div style={styles.ticketLabel}>{t("Numéro")}</div>
                        <div>{ticket?.ticket_order?.order_number}</div>
                      </div>
                      <div>
                        <div style={styles.ticketLabel}>{t("Caissier")}</div>
                        <div>
                          {ticket?.ticket_order?.user_id?.nom || ""}{" "}
                          {ticket?.ticket_order?.user_id?.prenom || ""}
                        </div>
                      </div>
                      <div>
                        <div style={styles.ticketLabel}>{t("Date")}</div>
                        <div>
                          {formatDateAndTime(
                            new Date(ticket.ticket_order.date),
                          )}
                        </div>
                      </div>
                    </div>

                    <table style={styles.ticketTable}>
                      <thead>
                        <tr>
                          <th>{t("Désignation")}</th>
                          <th>{t("Qté")}</th>
                          <th>{t("Total")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ticket?.ticket_order.items.map(
                          (item: any, idx: number) =>
                            parseInt(item.quantity) !== 0 ? (
                              <tr key={idx}>
                                <td>{item.article.titre.fr}</td>
                                <td>{item.quantity}</td>
                                <td>
                                  {Number(
                                    item.quantity < 0
                                      ? item.prixNet
                                      : item.prix_total,
                                  )?.toFixed(3)}
                                </td>
                              </tr>
                            ) : null,
                        )}
                      </tbody>
                      <tfoot>
                        <tr style={styles.ticketTotalRow}>
                          <td colSpan={2}>{t("Total Net")}</td>
                          <td>
                            {Number(ticket?.ticket_order.totalNet)?.toFixed(3)}{" "}
                            TND
                          </td>
                        </tr>
                      </tfoot>
                    </table>

                    {ticket && ticket.main_order && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          marginTop: "16px",
                        }}
                      >
                        <Barcode value={ticket?.ticket_order.order_number} />
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div style={styles.emptyTicket}>
                  <i className="fa fa-receipt" style={{ fontSize: "48px" }}></i>
                  <p>{t("Sélectionnez une commande pour voir le ticket")}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Points Timeline */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={{ ...styles.sectionIcon, backgroundColor: "#10b981" }}>
              <i className="fa fa-line-chart" style={{ color: "white" }}></i>
            </div>
            <h3 style={styles.sectionTitle}>{t("Historique des points")}</h3>
          </div>
          <div style={styles.pointsTimeline}>
            {timeline.map((item: FideliteTimelineItem) => (
              <div key={item._id} style={styles.pointsItem}>
                <div
                  style={{
                    ...styles.pointsBadge,
                    backgroundColor:
                      item.type === "earned" ? "#d1fae5" : "#fee2e2",
                    color: item.type === "earned" ? "#065f46" : "#991b1b",
                  }}
                >
                  <i
                    className={`fa fa-${
                      item.type === "earned" ? "plus" : "minus"
                    }`}
                  ></i>{" "}
                  {item.points}
                </div>
                <div style={styles.pointsInfo}>
                  <div style={styles.pointsTitle}>
                    {item.type === "earned"
                      ? t("Points gagnés")
                      : t("Points convertis")}
                  </div>
                  <div style={styles.pointsDate}>
                    {formatDateAndTime(item.date)}
                  </div>
                  {item.amount && <div>{item.amount} TND</div>}
                </div>
              </div>
            ))}
            {timeline.length === 0 && (
              <p style={styles.emptyMessage}>
                {t("Aucun historique de points")}
              </p>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

// Helper Components
const StatCard = ({ title, value, icon, color }: any) => (
  <div className="stat-card" style={styles.statCard}>
    <div style={{ ...styles.statIcon, backgroundColor: color }}>
      <i
        className={`fa ${icon}`}
        style={{ color: "white", fontSize: "20px" }}
      ></i>
    </div>
    <div style={styles.statContent}>
      <div style={styles.statTitle}>{title}</div>
      <div style={styles.statValue}>{value}</div>
    </div>
  </div>
);

const InfoRow = ({ label, value }: any) => (
  <div style={styles.infoRow}>
    <span style={styles.infoLabel}>{label}</span>
    <span style={styles.infoValue}>{value || "—"}</span>
  </div>
);

const styles: { [key: string]: React.CSSProperties } = {
  modalHeader: {
    borderBottom: "1px solid #e5e7eb",
    padding: "20px 24px",
    backgroundColor: "#f9fafb",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  modalTitle: {
    display: "flex",
    alignItems: "center",
    fontSize: "20px",
    fontWeight: 600,
    color: "#111827",
  },
  clientName: {
    color: "#3b82f6",
    fontWeight: 700,
  },
  closeButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "8px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#6b7280",
  },
  modalBody: {
    padding: "24px",
    backgroundColor: "#ffffff",
    maxHeight: "80vh",
    overflowY: "auto" as const,
  },
  section: {
    marginBottom: "32px",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "20px",
    borderBottom: "2px solid #e5e7eb",
    paddingBottom: "12px",
  },
  sectionIcon: {
    width: "32px",
    height: "32px",
    backgroundColor: "#eff6ff",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#3b82f6",
  },
  sectionTitle: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#111827",
    margin: 0,
  },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "16px",
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "12px 16px",
    backgroundColor: "#f9fafb",
    borderRadius: "12px",
  },
  infoLabel: {
    fontSize: "13px",
    color: "#6b7280",
  },
  infoValue: {
    fontSize: "15px",
    fontWeight: 500,
    color: "#111827",
  },
  fidelityCards: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "16px",
  },
  fidelityCard: {
    backgroundColor: "#f9fafb",
    borderRadius: "16px",
    padding: "20px",
    textAlign: "center" as const,
    cursor: "pointer",
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  fidelityCardValue: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#111827",
    marginTop: "12px",
  },
  fidelityCardLabel: {
    fontSize: "13px",
    color: "#6b7280",
    marginTop: "4px",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "16px",
  },
  statCard: {
    backgroundColor: "#f9fafb",
    borderRadius: "16px",
    padding: "16px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    cursor: "pointer",
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  statIcon: {
    width: "44px",
    height: "44px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  statContent: {
    flex: 1,
  },
  statTitle: {
    fontSize: "12px",
    color: "#6b7280",
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
  },
  statValue: {
    fontSize: "18px",
    fontWeight: 700,
    color: "#111827",
  },
  historyContainer: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "24px",
  },
  timelineColumn: {
    borderRight: "1px solid #e5e7eb",
    paddingRight: "24px",
  },
  timeline: {
    maxHeight: "400px",
    overflowY: "auto" as const,
  },
  timelineItem: {
    display: "flex",
    gap: "16px",
    padding: "16px",
    marginBottom: "8px",
    borderRadius: "12px",
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
  timelineItemActive: {
    backgroundColor: "#eff6ff",
  },
  timelineDot: {
    width: "12px",
    height: "12px",
    borderRadius: "50%",
    backgroundColor: "#3b82f6",
    marginTop: "4px",
    flexShrink: 0,
  },
  timelineContent: {
    flex: 1,
  },
  timelineDate: {
    fontSize: "12px",
    color: "#6b7280",
    marginBottom: "4px",
  },
  timelineTitle: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#111827",
    marginBottom: "2px",
  },
  timelineAmount: {
    fontSize: "13px",
    color: "#10b981",
    fontWeight: 600,
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "16px",
    marginTop: "20px",
    paddingTop: "16px",
    borderTop: "1px solid #e5e7eb",
  },
  pageButton: {
    padding: "8px",
    borderRadius: "8px",
    border: "1px solid #e5e7eb",
    backgroundColor: "white",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  pageButtonDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  pageInfo: {
    fontSize: "14px",
    color: "#6b7280",
  },
  ticketColumn: {
    paddingLeft: "24px",
  },
  ticketContainer: {
    backgroundColor: "#f9fafb",
    borderRadius: "16px",
    overflow: "hidden",
  },
  ticketHeader: {
    backgroundColor: "#111827",
    color: "white",
    padding: "16px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontWeight: 600,
  },
  ticketContent: {
    padding: "16px",
  },
  ticketInfo: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "12px",
    marginBottom: "20px",
    paddingBottom: "16px",
    borderBottom: "1px dashed #e5e7eb",
  },
  ticketLabel: {
    fontSize: "11px",
    color: "#6b7280",
    marginBottom: "4px",
  },
  ticketTable: {
    width: "100%",
    fontSize: "13px",
    borderCollapse: "collapse" as const,
  },
  ticketTotalRow: {
    borderTop: "1px solid #e5e7eb",
    fontWeight: 700,
    backgroundColor: "#f3f4f6",
  },
  emptyTicket: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    padding: "48px",
    color: "#9ca3af",
    textAlign: "center" as const,
    gap: "12px",
  },
  pointsTimeline: {
    maxHeight: "300px",
    overflowY: "auto" as const,
  },
  pointsItem: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "12px",
    borderBottom: "1px solid #f3f4f6",
  },
  pointsBadge: {
    padding: "6px 12px",
    borderRadius: "20px",
    fontSize: "13px",
    fontWeight: 600,
    minWidth: "80px",
    textAlign: "center" as const,
  },
  pointsInfo: {
    flex: 1,
  },
  pointsTitle: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#111827",
  },
  pointsDate: {
    fontSize: "12px",
    color: "#6b7280",
  },
  emptyMessage: {
    textAlign: "center" as const,
    color: "#6b7280",
    padding: "32px",
  },
};

export default FicheClient;
